import { SocialChannelEnum } from '../../../common/general-types/briefs.dto';

export type QuickBriefStep = 'focus' | 'format' | 'network' | 'thinking' | 'review' | 'published' | 'draft';
export type QuickBriefFocus = 'brand' | 'product';
export type QuickBriefFormat = 'ugc' | 'influencer';
export type QuickBriefNetworkId = 'instagram' | 'tiktok' | 'youtube' | 'snapchat';

export interface QuickBriefNetworkOption {
  id: QuickBriefNetworkId;
  name: string;
  formats: string;
  deliverable: string;
  channels: SocialChannelEnum[];
}

export interface QuickBriefProduct {
  name: string;
  description: string;
  price: string;
  currency: string;
  imageUrl: string;
  sourceUrl: string;
}

export interface QuickBriefConcept {
  name: string;
  hook: string;
  description: string;
}

export interface GeneratedQuickBrief {
  title: string;
  summary: string;
  keyMessages: string[];
  concepts: QuickBriefConcept[];
  dos: string[];
  donts: string[];
  creatorProfile: string;
  niches: string[];
}

export interface ChannelPlan {
  primary: SocialChannelEnum;
  cross: SocialChannelEnum[];
}

export const QUICK_BRIEF_TIMELINE = '14 days';

export const UGC_DELIVERABLES = [
  '3 × vertical videos 20–45s',
  '6 × lifestyle photos',
  'Raw footage included',
  '12-month paid usage rights',
];

export const QUICK_BRIEF_NETWORKS: QuickBriefNetworkOption[] = [
  {
    id: 'instagram',
    name: 'Instagram',
    formats: 'Reels, Stories, Posts',
    deliverable: '1 × Instagram Reel + 3 × Stories',
    channels: [SocialChannelEnum['Instagram Reel'], SocialChannelEnum['Instagram Story']],
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    formats: 'Short-form video',
    deliverable: '1 × TikTok video, 21–45 seconds',
    channels: [SocialChannelEnum['TikTok Post']],
  },
  {
    id: 'youtube',
    name: 'YouTube',
    formats: 'Shorts, integrations',
    deliverable: '1 × YouTube Short',
    channels: [SocialChannelEnum['YouTube Shorts']],
  },
  {
    id: 'snapchat',
    name: 'Snapchat',
    formats: 'Spotlight, Stories',
    deliverable: '1 × Snapchat Spotlight video',
    channels: [SocialChannelEnum['Snapchat Spotlight']],
  },
];

const NETWORK_BY_ID = new Map(QUICK_BRIEF_NETWORKS.map((network) => [network.id, network]));

export function displayDomain(website: string | null | undefined): string {
  const raw = (website ?? '').trim();
  if (!raw) {
    return '';
  }
  try {
    const withProtocol = raw.includes('://') ? raw : `https://${raw}`;
    return new URL(withProtocol).hostname.replace(/^www\./, '');
  } catch {
    return raw.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0] ?? '';
  }
}

export function productUrlError(raw: string): string {
  const value = raw.trim();
  if (!/[a-z0-9-]+\.[a-z]{2,}/i.test(value)) {
    return 'Enter the full URL of your product page.';
  }
  return '';
}

export function normaliseProductUrl(raw: string): string {
  const value = raw.trim();
  if (!value) {
    return '';
  }
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

export function manualProductError(product: QuickBriefProduct): string {
  if (!product.name.trim()) {
    return 'Enter the product name.';
  }
  if (!product.description.trim()) {
    return 'Enter a short description.';
  }
  if (!product.imageUrl.trim()) {
    return 'Add a product image.';
  }
  const price = Number(product.price.trim());
  if (!Number.isFinite(price) || price < 0) {
    return 'Enter a price.';
  }
  if (price > 0 && !product.currency.trim()) {
    return 'Choose a currency.';
  }
  return '';
}

export function channelPlan(selected: QuickBriefNetworkId[]): ChannelPlan | null {
  const channels = selected.flatMap((id) => NETWORK_BY_ID.get(id)?.channels ?? []);
  const primary = channels[0];
  if (primary === undefined) {
    return null;
  }
  return { primary, cross: channels.slice(1) };
}

export function deliverableLines(format: QuickBriefFormat | null, selected: QuickBriefNetworkId[]): string[] {
  if (format === 'ugc') {
    return [...UGC_DELIVERABLES];
  }
  return selected.flatMap((id) => {
    const line = NETWORK_BY_ID.get(id)?.deliverable;
    return line ? [line] : [];
  });
}

export function networkNames(selected: QuickBriefNetworkId[]): string {
  return selected
    .map((id) => NETWORK_BY_ID.get(id)?.name)
    .filter((name): name is string => Boolean(name))
    .join(' and ');
}

export function progressFor(
  step: QuickBriefStep,
  format: QuickBriefFormat | null,
): { current: number; total: number } | null {
  const total = format === 'ugc' ? 2 : 3;
  if (step === 'focus') {
    return { current: 1, total };
  }
  if (step === 'format') {
    return { current: 2, total };
  }
  if (step === 'network') {
    return { current: 3, total: 3 };
  }
  return null;
}

export function previousQuestion(step: QuickBriefStep, format: QuickBriefFormat | null): QuickBriefStep | null {
  if (step === 'format') {
    return 'focus';
  }
  if (step === 'network') {
    return 'format';
  }
  if (step === 'review') {
    return format === 'influencer' ? 'network' : 'format';
  }
  return null;
}

export function thinkingStageLabels(input: {
  domain: string;
  subject: string;
  brandName: string;
  format: QuickBriefFormat | null;
  focus: QuickBriefFocus | null;
  networkLabel: string;
}): string[] {
  const domain = input.domain || 'your site';
  const subject = input.subject || input.brandName || 'your brand';
  const story = input.focus === 'product'
    ? `Studying the ${subject}`
    : `Mapping the ${input.brandName || 'brand'} story`;
  const writing = input.format === 'ugc'
    ? 'Writing UGC concepts and shot lists'
    : `Writing concepts for ${input.networkLabel || 'your channels'}`;
  return [
    `Reading ${domain}`,
    'Analysing your brand tone and audience',
    story,
    writing,
    'Setting creator requirements and budget',
  ];
}

export function listHtml(items: string[]): string {
  const rows = items
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
    .map((item) => `<li class="text-xs text-gray-900 dark:text-white mb-1">${escapeHtml(item)}</li>`)
    .join('');
  return `<ul class="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 mb-4">${rows}</ul>`;
}

export function stepsHtml(input: {
  summary: string;
  keyMessages: string[];
  deliverables: string[];
  creatorProfile: string;
  niches: string[];
}): string {
  const niches = input.niches.filter((niche) => niche.trim().length > 0).join(', ');
  const creator = [input.creatorProfile.trim(), niches ? `Niches: ${niches}` : '']
    .filter((line) => line.length > 0)
    .join(' ');
  return [
    heading('Overview'),
    paragraph(input.summary),
    heading('Key messages'),
    listHtml(input.keyMessages),
    heading('Deliverables'),
    listHtml(input.deliverables),
    heading("Creators we'll match"),
    paragraph(creator || 'Creators matched to this brief.'),
    paragraph(`Timeline: ${QUICK_BRIEF_TIMELINE}.`),
  ].join('');
}

export function conceptRecords(concepts: QuickBriefConcept[]): { id: number; name: string; description: string }[] {
  return concepts.slice(0, 3).map((concept, index) => {
    const hook = concept.hook.trim();
    const description = concept.description.trim();
    const combined = hook ? `"${hook}" ${description}`.trim() : description;
    return { id: index, name: concept.name.trim(), description: combined };
  });
}

export function parseGeneratedBrief(value: unknown): GeneratedQuickBrief | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const record = value as Record<string, unknown>;
  const title = text(record['title']);
  const summary = text(record['summary']);
  const keyMessages = textList(record['keyMessages']);
  const concepts = conceptList(record['concepts']);
  const dos = textList(record['dos']);
  const donts = textList(record['donts']);
  if (!title || !summary || keyMessages.length === 0 || concepts.length === 0 || dos.length === 0 || donts.length === 0) {
    return null;
  }
  return {
    title,
    summary,
    keyMessages,
    concepts,
    dos,
    donts,
    creatorProfile: text(record['creatorProfile']),
    niches: textList(record['niches']),
  };
}

export function parseBriefJson(raw: string): unknown {
  const trimmed = raw.trim().replace(/^```(?:json)?/i, '').replace(/```$/, '').trim();
  return JSON.parse(trimmed);
}

function conceptList(value: unknown): QuickBriefConcept[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') {
      return [];
    }
    const record = item as Record<string, unknown>;
    const name = text(record['name']);
    if (!name) {
      return [];
    }
    return [{ name, hook: text(record['hook']), description: text(record['description']) }];
  });
}

function textList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((item) => text(item)).filter((item) => item.length > 0);
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function heading(label: string): string {
  return `<h2 class="text-md line-6 font-medium text-gray-900 dark:text-white mb-1">${escapeHtml(label)}</h2>`;
}

function paragraph(value: string): string {
  return `<p class="text-sm text-gray-600 dark:text-gray-400 mb-4">${escapeHtml(value)}</p>`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
